import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, PawPrint, FolderOpen, Upload, Download, Trash2, Pencil,
  FileText, FileImage, File as FileIcon, Tag, UploadCloud, X as XIcon, Save,
  Camera, RefreshCw,
} from 'lucide-react';
import { formatDate } from '../../utils/dateFormat';
import { logger } from '../../utils/logger';
import { CameraScanner } from '../../components/admin/CameraScanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_MB = 25;

const DOC_TYPE_OPTIONS = [
  { value: 'veterinary_health_certificate', label: 'Veterinary Health Certificate' },
  { value: 'vaccination_records',           label: 'Vaccination Records' },
  { value: 'microchip_documentation',       label: 'Microchip Documentation' },
  { value: 'pet_passport',                  label: 'Pet Passport' },
  { value: 'import_permit',                 label: 'Import Permit' },
  { value: 'rabies_titer_test',             label: 'Rabies Titer Test' },
  { value: 'pet_photos',                    label: 'Pet Photos' },
  { value: 'owner_passport',                label: 'Owner Passport' },
  { value: 'flight_booking',                label: 'Flight Booking' },
  { value: 'boarding_records',              label: 'Boarding Records' },
  { value: 'insurance',                     label: 'Insurance' },
  { value: 'breed_certificate',             label: 'Breed Certificate' },
  { value: 'other',                         label: 'Other' },
];

const labelForType = (t) =>
  DOC_TYPE_OPTIONS.find((o) => o.value === t)?.label || (t ? t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Other');

const fileIconFor = (name, contentType) => {
  const ct = (contentType || '').toLowerCase();
  if (ct.startsWith('image/')) return FileImage;
  if (ct.includes('pdf') || name?.toLowerCase().endsWith('.pdf')) return FileText;
  return FileIcon;
};

const fmtSize = (b) => {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MB`;
};

export const PetFolder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const fileInputRef = useRef(null);

  const [pet, setPet] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPet, setEditingPet] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]); // [{file, progress, error, id}]
  const [isDragging, setIsDragging] = useState(false);
  const [showMetaModal, setShowMetaModal] = useState(null); // pending file awaiting type/tags
  const [editingDoc, setEditingDoc] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [scannerOpen, setScannerOpen] = useState(false);

  useEffect(() => {
    if (!password) { navigate('/admin/login'); return; }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const authHeaders = { 'Authorization': `Basic ${btoa(':' + password)}` };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [petRes, docsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/pets/${id}`, { headers: authHeaders }),
        fetch(`${API_URL}/api/admin/pets/${id}/documents`, { headers: authHeaders }),
      ]);
      if (petRes.ok) setPet(await petRes.json());
      else if (petRes.status === 404) { alert('Pet not found'); navigate('/admin/pets'); }
      if (docsRes.ok) setDocs(await docsRes.json());
    } catch (err) {
      logger.error('pet folder fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  // ---- Upload helpers ---------------------------------------------------
  const queueFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const first = files[0];
    if (first.size > MAX_FILE_MB * 1024 * 1024) {
      alert(`"${first.name}" exceeds ${MAX_FILE_MB}MB. Please compress or choose another file.`);
      return;
    }
    // If multiple, show the meta modal for each sequentially
    setShowMetaModal({ file: first, remaining: files.slice(1) });
  };

  const onPickFiles = (e) => queueFiles(e.target.files);

  const onDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = () => setIsDragging(false);
  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    queueFiles(e.dataTransfer.files);
  };

  const doUpload = async ({ file, document_type, tags, notes }) => {
    const jobId = `${Date.now()}-${file.name}`;
    setUploadQueue((q) => [...q, { id: jobId, name: file.name, progress: 0, error: null }]);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('document_type', document_type || 'other');
      form.append('tags', tags || '');
      if (notes) form.append('notes', notes);

      // Use XHR for real upload progress
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/admin/pets/${id}/documents`);
        xhr.setRequestHeader('Authorization', `Basic ${btoa(':' + password)}`);
        xhr.upload.onprogress = (ev) => {
          if (!ev.lengthComputable) return;
          const pct = Math.round((ev.loaded / ev.total) * 100);
          setUploadQueue((q) => q.map((j) => j.id === jobId ? { ...j, progress: pct } : j));
        };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300
          ? resolve(JSON.parse(xhr.responseText))
          : reject(new Error(xhr.responseText || `HTTP ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(form);
      });

      setDocs((prev) => [result, ...prev]);
      // Fade the job off the list after a short delay
      setTimeout(() => {
        setUploadQueue((q) => q.filter((j) => j.id !== jobId));
      }, 1500);
    } catch (err) {
      logger.error('upload error:', err);
      setUploadQueue((q) => q.map((j) => j.id === jobId ? { ...j, error: err.message || 'Upload failed' } : j));
    }
  };

  const confirmMetaAndUpload = async (meta) => {
    if (!showMetaModal) return;
    const { file, remaining = [] } = showMetaModal;
    setShowMetaModal(null);
    await doUpload({ file, ...meta });
    if (remaining.length) {
      setShowMetaModal({ file: remaining[0], remaining: remaining.slice(1) });
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.file_name}"? This cannot be undone.`)) return;
    const res = await fetch(`${API_URL}/api/admin/documents/${doc.id}`, {
      method: 'DELETE', headers: authHeaders,
    });
    if (res.ok) setDocs((prev) => prev.filter((d) => d.id !== doc.id));
    else alert('Delete failed');
  };

  const handleDownload = async (doc) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/documents/${doc.id}/download`, { headers: authHeaders });
      if (!res.ok) { alert('Download failed'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = doc.file_name;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Download failed'); }
  };

  const savePet = async (updates) => {
    const res = await fetch(`${API_URL}/api/admin/pets/${id}`, {
      method: 'PUT',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    if (res.ok) { setPet(await res.json()); setEditingPet(false); }
    else alert('Save failed');
  };

  const saveDoc = async (docId, updates) => {
    const res = await fetch(`${API_URL}/api/admin/documents/${docId}`, {
      method: 'PUT',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    if (res.ok) {
      const fresh = await res.json();
      setDocs((prev) => prev.map((d) => d.id === docId ? fresh : d));
      setEditingDoc(null);
    } else alert('Save failed');
  };

  const replaceDocFile = async (docId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_URL}/api/admin/documents/${docId}/replace`, {
      method: 'POST',
      headers: authHeaders, // browser sets multipart Content-Type
      body: fd,
    });
    if (res.ok) {
      const fresh = await res.json();
      setDocs((prev) => prev.map((d) => d.id === docId ? fresh : d));
      // Keep modal open with fresh data so user can also tweak metadata
      setEditingDoc(fresh);
    } else {
      const err = await res.json().catch(() => ({}));
      alert(`Replace failed: ${err.detail || res.status}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!pet) return null;

  // Group documents by document_type
  const filtered = typeFilter === 'all' ? docs : docs.filter((d) => d.document_type === typeFilter);
  const groupedDocs = filtered.reduce((acc, d) => {
    const k = d.document_type || 'other';
    (acc[k] = acc[k] || []).push(d);
    return acc;
  }, {});

  const typeCounts = docs.reduce((acc, d) => {
    acc[d.document_type || 'other'] = (acc[d.document_type || 'other'] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <button
            onClick={() => navigate('/admin/pets')}
            className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" /> All Pets
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setScannerOpen(true)}
              data-testid="scan-btn"
              className="px-4 py-2 text-sm border border-primary text-primary rounded-lg hover:bg-primary/5 flex items-center gap-2"
            >
              <Camera className="w-4 h-4" /> Scan Document
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-btn"
              className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
            >
              <Upload className="w-4 h-4" /> Upload Document
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,application/pdf,.pdf,.doc,.docx,.heic,.heif,.webp,.png,.jpg,.jpeg"
            onChange={onPickFiles}
            className="hidden"
            data-testid="file-input"
          />
        </div>
      </div>

      <div className="container mx-auto px-6 py-6 space-y-6">
        {/* Pet Info Card */}
        <div className="bg-white border border-border rounded-xl p-6" data-testid="pet-info-card">
          {editingPet ? (
            <PetInfoEditor pet={pet} onCancel={() => setEditingPet(false)} onSave={savePet} />
          ) : (
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <PawPrint className="w-8 h-8" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground" data-testid="pet-name">{pet.name}</h1>
                  <p className="text-sm text-foreground/70">
                    {pet.species}{pet.breed ? ` · ${pet.breed}` : ''}
                    {pet.sex ? ` · ${pet.sex}` : ''}
                    {pet.date_of_birth ? ` · DOB ${pet.date_of_birth}` : ''}
                    {pet.age ? ` · ${pet.age}` : ''}
                  </p>
                  <p className="text-xs text-foreground/60 mt-1">
                    Client: <span className="font-medium">{pet.client_name}</span>
                    {pet.client_email && <span className="font-mono"> · {pet.client_email}</span>}
                  </p>
                  {pet.microchip_number && (
                    <p className="text-xs text-foreground/60 mt-1">Microchip: <span className="font-mono">{pet.microchip_number}</span></p>
                  )}
                  {pet.color_markings && (
                    <p className="text-xs text-foreground/60 mt-1">Markings: {pet.color_markings}</p>
                  )}
                  {pet.notes && (
                    <p className="text-sm text-foreground/80 mt-3 bg-gray-50 rounded-lg p-3 border border-border">
                      {pet.notes}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setEditingPet(true)}
                data-testid="edit-pet-btn"
                className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-gray-50 flex items-center gap-1"
              >
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
            </div>
          )}
        </div>

        {/* Drop Zone + Upload Queue */}
        <div
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          data-testid="drop-zone"
          className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
            isDragging ? 'border-primary bg-primary/5' : 'border-border bg-white'
          }`}
        >
          <UploadCloud className={`w-10 h-10 mx-auto mb-2 ${isDragging ? 'text-primary' : 'text-foreground/30'}`} />
          <p className="text-sm font-medium text-foreground">
            Drag & drop files here, or{' '}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-primary underline"
            >
              browse
            </button>
          </p>
          <p className="text-xs text-foreground/60 mt-1">
            Up to {MAX_FILE_MB}MB each · PDFs, images (phone camera scans welcome), Word docs
          </p>

          {uploadQueue.length > 0 && (
            <div className="mt-4 space-y-2 text-left max-w-lg mx-auto" data-testid="upload-queue">
              {uploadQueue.map((job) => (
                <div key={job.id} className="bg-white border border-border rounded-lg p-2">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium truncate">{job.name}</span>
                    <span className={job.error ? 'text-red-600' : 'text-foreground/60'}>
                      {job.error ? 'Failed' : `${job.progress}%`}
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${job.error ? 'bg-red-400' : 'bg-primary'} transition-all`}
                      style={{ width: `${job.progress}%` }}
                    />
                  </div>
                  {job.error && <p className="text-xs text-red-600 mt-1">{job.error}</p>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Document type filter */}
        {docs.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="doc-filter-chips">
            <button
              onClick={() => setTypeFilter('all')}
              className={`px-3 py-1 text-xs font-medium rounded-full border ${
                typeFilter === 'all' ? 'bg-primary text-white border-primary' : 'bg-white border-border hover:bg-gray-50'
              }`}
            >
              All ({docs.length})
            </button>
            {Object.entries(typeCounts).map(([t, count]) => (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                data-testid={`filter-type-${t}`}
                className={`px-3 py-1 text-xs font-medium rounded-full border ${
                  typeFilter === t ? 'bg-primary text-white border-primary' : 'bg-white border-border hover:bg-gray-50'
                }`}
              >
                {labelForType(t)} ({count})
              </button>
            ))}
          </div>
        )}

        {/* Documents */}
        {docs.length === 0 ? (
          <div className="bg-white border border-border rounded-xl p-12 text-center">
            <FolderOpen className="w-16 h-16 text-foreground/30 mx-auto mb-3" />
            <h2 className="text-lg font-semibold mb-1">This folder is empty</h2>
            <p className="text-sm text-foreground/60">Drop a file above or click Upload Document.</p>
          </div>
        ) : Object.keys(groupedDocs).length === 0 ? (
          <p className="text-center text-foreground/60 py-6">No documents match this filter.</p>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedDocs).map(([type, items]) => (
              <section key={type} className="bg-white border border-border rounded-xl overflow-hidden">
                <header className="px-5 py-3 border-b border-border bg-gray-50 flex items-center gap-2">
                  <h2 className="font-semibold text-foreground">{labelForType(type)}</h2>
                  <span className="text-xs text-foreground/60">({items.length})</span>
                </header>
                <ul className="divide-y divide-border">
                  {items.map((doc) => {
                    const Icon = fileIconFor(doc.file_name, doc.content_type);
                    return (
                      <li key={doc.id} data-testid={`doc-row-${doc.id}`} className="px-5 py-3 flex items-center gap-4 hover:bg-gray-50">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-foreground truncate">{doc.file_name}</p>
                          <div className="flex items-center gap-2 text-xs text-foreground/60 flex-wrap">
                            <span>{fmtSize(doc.file_size)}</span>
                            <span>·</span>
                            <span>Uploaded {formatDate(doc.uploaded_at)}</span>
                            {doc.tags?.length > 0 && (
                              <>
                                <span>·</span>
                                {doc.tags.map((t) => (
                                  <span key={`${doc.id}-${t}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px]">
                                    <Tag className="w-2.5 h-2.5" /> {t}
                                  </span>
                                ))}
                              </>
                            )}
                          </div>
                          {doc.notes && <p className="text-xs text-foreground/70 mt-1">{doc.notes}</p>}
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleDownload(doc)} data-testid={`download-${doc.id}`} title="Download" className="p-2 text-foreground/60 hover:text-primary rounded-lg hover:bg-gray-100">
                            <Download className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditingDoc(doc)} data-testid={`edit-doc-${doc.id}`} title="Edit category/tags" className="p-2 text-foreground/60 hover:text-primary rounded-lg hover:bg-gray-100">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(doc)} data-testid={`delete-${doc.id}`} title="Delete" className="p-2 text-red-600 hover:text-red-700 rounded-lg hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>

      {showMetaModal && (
        <UploadMetaModal
          file={showMetaModal.file}
          onCancel={() => setShowMetaModal(null)}
          onConfirm={confirmMetaAndUpload}
        />
      )}

      {editingDoc && (
        <EditDocumentModal
          doc={editingDoc}
          onCancel={() => setEditingDoc(null)}
          onSave={(updates) => saveDoc(editingDoc.id, updates)}
          onReplaceFile={(file) => replaceDocFile(editingDoc.id, file)}
        />
      )}

      <CameraScanner
        open={scannerOpen}
        defaultName={pet?.name ? `${pet.name.replace(/\s+/g, '-').toLowerCase()}-scan` : 'pet-scan'}
        onClose={() => setScannerOpen(false)}
        onCapture={(file) => {
          setScannerOpen(false);
          setShowMetaModal({ file, remaining: [] });
        }}
      />
    </div>
  );
};

// =========================================================
// Pet Info Editor (inline)
// =========================================================
const SEX_OPTIONS = [
  'Intact Male',
  'Neutered Male',
  'Intact Female',
  'Spayed Female',
  'Unknown',
];

const deriveAgeFromDob = (dob) => {
  if (!dob) return '';
  try {
    const d = new Date(dob + 'T00:00:00');
    const now = new Date();
    const months =
      (now.getFullYear() - d.getFullYear()) * 12 +
      (now.getMonth() - d.getMonth()) -
      (now.getDate() < d.getDate() ? 1 : 0);
    if (months < 0) return '';
    if (months < 12) return `${months} month${months === 1 ? '' : 's'}`;
    const years = Math.floor(months / 12);
    const rem = months % 12;
    return rem === 0
      ? `${years} year${years === 1 ? '' : 's'}`
      : `${years}y ${rem}m`;
  } catch {
    return '';
  }
};

const PetInfoEditor = ({ pet, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: pet.name || '',
    species: pet.species || 'Dog',
    breed: pet.breed || '',
    date_of_birth: pet.date_of_birth || '',
    age: pet.age || '',
    sex: pet.sex || '',
    color_markings: pet.color_markings || '',
    microchip_number: pet.microchip_number || '',
    notes: pet.notes || '',
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const handleDobChange = (value) => {
    setForm((f) => ({
      ...f,
      date_of_birth: value,
      age: f.age && f.age.trim() ? f.age : deriveAgeFromDob(value),
    }));
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-3" lang="en-GB" spellCheck="true">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Labeled label="Name *">
          <input required value={form.name} onChange={(e) => update('name', e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
        </Labeled>
        <Labeled label="Species">
          <select value={form.species} onChange={(e) => update('species', e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg text-sm">
            {['Dog', 'Cat', 'Bird', 'Rabbit', 'Other'].map(s => <option key={s}>{s}</option>)}
          </select>
        </Labeled>
        <Labeled label="Breed">
          <input value={form.breed} onChange={(e) => update('breed', e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
        </Labeled>
        <Labeled label="Date of Birth">
          <input type="date" value={form.date_of_birth} onChange={(e) => handleDobChange(e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
        </Labeled>
        <Labeled label="Age">
          <input
            value={form.age}
            onChange={(e) => update('age', e.target.value)}
            placeholder="Auto-filled from DOB"
            data-testid="edit-pet-age"
            className="w-full px-3 py-2 border border-border rounded-lg text-sm"
          />
        </Labeled>
        <Labeled label="Sex / Reproductive Status">
          <select value={form.sex} onChange={(e) => update('sex', e.target.value)} data-testid="edit-pet-sex" className="w-full px-3 py-2 border border-border rounded-lg text-sm">
            <option value="">—</option>
            {SEX_OPTIONS.map(o => <option key={o}>{o}</option>)}
            {/* Preserve older values that may already be in the DB */}
            {form.sex && !SEX_OPTIONS.includes(form.sex) && <option value={form.sex}>{form.sex}</option>}
          </select>
        </Labeled>
        <Labeled label="Microchip" className="md:col-span-3">
          <input value={form.microchip_number} onChange={(e) => update('microchip_number', e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono" />
        </Labeled>
        <Labeled label="Markings" className="md:col-span-3">
          <input value={form.color_markings} onChange={(e) => update('color_markings', e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
        </Labeled>
        <Labeled label="Notes" className="md:col-span-3">
          <textarea rows="2" value={form.notes} onChange={(e) => update('notes', e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
        </Labeled>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-gray-50">Cancel</button>
        <button type="submit" data-testid="save-pet-btn" className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-1">
          <Save className="w-3.5 h-3.5" /> Save
        </button>
      </div>
    </form>
  );
};

const Labeled = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="block text-xs font-medium text-foreground/60 uppercase tracking-wide mb-1">{label}</span>
    {children}
  </label>
);

// =========================================================
// Upload metadata modal (category + tags prompt per file)
// =========================================================
const UploadMetaModal = ({ file, onCancel, onConfirm }) => {
  const [form, setForm] = useState({ document_type: 'other', tags: '', notes: '' });
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel} data-testid="upload-meta-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={(e) => { e.preventDefault(); onConfirm(form); }}>
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <h3 className="font-bold text-foreground">Categorise "{file.name}"</h3>
            <button type="button" onClick={onCancel} className="text-foreground/50"><XIcon className="w-4 h-4" /></button>
          </div>
          <div className="p-6 space-y-3">
            <Labeled label="Document type *">
              <select
                required
                value={form.document_type}
                onChange={(e) => setForm((f) => ({ ...f, document_type: e.target.value }))}
                data-testid="upload-type-select"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              >
                {DOC_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Labeled>
            <Labeled label="Tags (comma-separated, optional)">
              <input
                value={form.tags}
                onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                data-testid="upload-tags"
                placeholder="e.g. rabies, annual, cayman"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </Labeled>
            <Labeled label="Notes (optional)">
              <textarea
                rows="2"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </Labeled>
            <p className="text-xs text-foreground/60">
              {fmtSize(file.size)} · {file.type || 'unknown type'}
            </p>
          </div>
          <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
            <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" data-testid="confirm-upload-btn" className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-1">
              <Upload className="w-3.5 h-3.5" /> Upload
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =========================================================
// Edit document metadata modal — rename, re-categorise, swap file
// =========================================================
const EditDocumentModal = ({ doc, onCancel, onSave, onReplaceFile }) => {
  const [form, setForm] = useState({
    file_name: doc.file_name || '',
    document_type: doc.document_type || 'other',
    tags: (doc.tags || []).join(', '),
    notes: doc.notes || '',
  });
  const replaceRef = useRef(null);
  const [replacing, setReplacing] = useState(false);

  const handleReplace = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !onReplaceFile) return;
    if (!window.confirm(`Replace "${doc.file_name}" with "${file.name}"? The old file will be permanently deleted.`)) {
      e.target.value = '';
      return;
    }
    setReplacing(true);
    try {
      await onReplaceFile(file);
    } finally {
      setReplacing(false);
      e.target.value = '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel} data-testid="edit-doc-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} lang="en-GB" spellCheck="true">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <h3 className="font-bold text-foreground">Edit document</h3>
            <button type="button" onClick={onCancel} className="text-foreground/50"><XIcon className="w-4 h-4" /></button>
          </div>
          <div className="p-6 space-y-3">
            <Labeled label="File name">
              <input
                value={form.file_name}
                onChange={(e) => setForm((f) => ({ ...f, file_name: e.target.value }))}
                data-testid="edit-doc-filename"
                placeholder="e.g. bella-rabies-2026.pdf"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
              />
            </Labeled>
            <Labeled label="Category">
              <select value={form.document_type} onChange={(e) => setForm((f) => ({ ...f, document_type: e.target.value }))} className="w-full px-3 py-2 border border-border rounded-lg text-sm">
                {DOC_TYPE_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
              </select>
            </Labeled>
            <Labeled label="Tags (comma-separated)">
              <input value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
            </Labeled>
            <Labeled label="Notes">
              <textarea rows="2" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
            </Labeled>

            {/* Replace file section */}
            <div className="bg-gray-50 border border-border rounded-lg p-3">
              <p className="text-xs font-medium text-foreground/70 uppercase tracking-wide mb-1">Replace file</p>
              <p className="text-xs text-foreground/60 mb-2">
                Uploaded a better scan or need to swap the content? Pick a new file — the old one is deleted.
              </p>
              <input
                ref={replaceRef}
                type="file"
                onChange={handleReplace}
                className="hidden"
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                data-testid="replace-doc-input"
              />
              <button
                type="button"
                onClick={() => replaceRef.current?.click()}
                disabled={replacing}
                data-testid="replace-doc-btn"
                className="px-3 py-1.5 text-xs border border-amber-300 bg-amber-50 text-amber-800 rounded-lg hover:bg-amber-100 disabled:opacity-50 flex items-center gap-1"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${replacing ? 'animate-spin' : ''}`} />
                {replacing ? 'Uploading…' : 'Choose replacement file'}
              </button>
            </div>
          </div>
          <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
            <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-1">
              <Save className="w-3.5 h-3.5" /> Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
